import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';

export type FeedbackActions = 'created' | 'updated' | 'deleted' | 'saved' | 'imported' | 'queued' | 'processed' | 'refreshed' | 'loaded';
export type FeedbackVariant = 'error' | 'warn';
export type FeedbackContext = 'root' | 'flyout' | 'modal' | 'auto';
export type FeedbackMessage = { message: string; id: string; variant: FeedbackVariant; context: FeedbackContext };

export interface FeedbackSuccessOptions {
	buttonText: string;
	isPlural: boolean;
}

export const FEEDBACK_SUCCESS_DEFAULT_OPTIONS: FeedbackSuccessOptions = {
	buttonText: 'Ok',
	isPlural: false,
};

@Injectable({
	providedIn: 'root',
})
export class FeedbackService {
	messages$ = new Subject<FeedbackMessage[]>();
	messages: FeedbackMessage[] = [];
	context: FeedbackContext = 'root';

	constructor(private readonly snackBar: MatSnackBar) {}

	/**
	 * Set the internal context based on the external
	 * context switching. Use with observables to update
	 * the internal context when the flyout or modal opens.
	 * Used when the user supplied context is set to `auto`.
	 */
	computeContext(context: FeedbackContext, open: boolean): void {
		this.context = open ? context : 'root';
	}

	/**
	 * Compute a success message for each different type of action.
	 * Has option to toggle plural language. Keeps messages consistent.
	 *
	 * @param action Approved verbs for successful actions
	 * @param noun Any object which was changed
	 * @param isPlural Flag to change `was` to `were`
	 * @returns string
	 */
	computeSuccessMessage(action: FeedbackActions, noun: string, isPlural: boolean): string {
		return `${noun} ${isPlural ? 'were' : 'was'} ${action} successfully`;
	}

	/**
	 * Display a Material Design SnackBar with a consistent messages
	 * based on the action and noun.
	 * Updated payroll - `this.success('updated', 'Payroll');`
	 * Created multiple employees - `this.success('created', 'Employees', { isPlural: true });`
	 *
	 * @param action Approved verbs for successful actions
	 * @param noun Any object which was changed
	 * @param options Options for triggering plural verbiage or custom close button
	 */
	success(action: FeedbackActions, noun: string, options: Partial<FeedbackSuccessOptions> = {}): void {
		const config = { ...FEEDBACK_SUCCESS_DEFAULT_OPTIONS, ...options };
		this.snackBar.open(this.computeSuccessMessage(action, noun, config.isPlural), config.buttonText);
	}

	customSuccess(message: string, options: Partial<FeedbackSuccessOptions> = {}): void {
		const config = { ...FEEDBACK_SUCCESS_DEFAULT_OPTIONS, ...options };
		this.snackBar.open(message, config.buttonText);
	}

	error(message: string, context: FeedbackContext = 'auto'): string {
		return this.message(message, 'error', context);
	}

	warn(message: string, context: FeedbackContext = 'auto'): string {
		return this.message(message, 'warn', context);
	}

	message(message: string, variant: FeedbackVariant, context: FeedbackContext = 'auto'): string {
		const id = new Date().toISOString();
		this.messages.push({
			message,
			id,
			variant,
			context: context === 'auto' ? this.context : context,
		});
		this.messages$.next(this.messages);
		return id;
	}

	/**
	 * Clear either a single item from the message list or
	 * clear all the messages which match the provided varient.
	 * Clear one - `this.feedback.clear('abc');`
	 * Clear all errors - `this.feedback.clear(undefined, 'error');`
	 *
	 * @param removeId optional id of the message to remove
	 * @param variant if no id is provided clear all matching variants
	 */
	clear(removeId: string, variant: FeedbackVariant, context: FeedbackContext = 'root'): void {
		this.messages = this.messages.filter(({ id, variant: v, context: c }) => {
			if (removeId) {
				return id !== removeId;
			}

			return variant !== v || c !== context;
		});
		this.messages$.next(this.messages);
	}

	/**
	 * Clear all the message regardless of the context.
	 * Set the internal context to root when route changes.
	 * Used when a route changes, see `app.component.ts`
	 */
	clearAll(): void {
		this.context = 'root';
		this.messages = [];
		this.messages$.next(this.messages);
	}
}
